<template>
  <main class="w-full max-w-[1100px] mx-auto flex flex-col gap-6 px-4 pb-32 xl:px-0 xl:pb-0">
    <LoaderOverlay :active="loadingInfo" />
    <section
      v-if="hasSales"
      class="flex flex-col gap-6 xl:flex-row xl:items-start"
    >
      <div class="flex-1 space-y-6">
        <article
          class="bg-white border border-black/10 rounded-[2.4rem] p-8 space-y-4"
        >
          <div v-if="loadingInfo" class="sr-only" aria-live="polite">
            {{ $t('Loading') }}
          </div>
          <div
            v-else-if="infoError"
            class="flex flex-col gap-3 rounded-[1.6rem] border border-red-200 bg-red-50 p-4 text-[1.3rem] text-red-700"
          >
            <p class="m-0 font-semibold text-[1.4rem] text-red-800">
              {{ $t('There was a problem') }}
            </p>
            <p class="m-0 whitespace-pre-line">
              {{ infoError }}
            </p>
          </div>
          <div v-else-if="currentSale" class="flex flex-col gap-4">
            <CheckoutSummaryMobile
              v-model:open="mobileSummaryOpen"
              :sale-cards="saleDoctorCards"
              :total-due="totalDue"
              :format-amount="formatAmount"
              @remove-sale="handleRemoveSale"
            />
            <div class="space-y-4">
              <CheckoutPersonalInfo
                :name="personalName"
                :surname="personalSurname"
                :comment="personalComment"
                @update:comment="personalComment = $event"
              />
              <CheckoutPaymentMethods
                :methods="paymentMethods"
                :selected-id="selectedPaymentMethodId"
                :allow-tokenization="Boolean(selectedPaymentMethod?.allowTokenization)"
                :force-save="Boolean(selectedPaymentMethod?.forceSaveCreditCard)"
                :save-card="savePaymentMethod"
                :get-icon-path="getPaymentIconPath"
                @select="selectPaymentMethod"
                @update:saveCard="savePaymentMethod = $event"
              />
              <CheckoutLegalOptions
                :accept-legal="acceptLegal"
                :accept-medical="acceptMedical"
                :accept-marketing="acceptMarketing"
                :legal-error="legalError"
                @update:acceptLegal="acceptLegal = $event"
                @update:acceptMedical="acceptMedical = $event"
                @update:acceptMarketing="acceptMarketing = $event"
                @open-legal="openLegalModal"
              />
              <CheckoutPaymentForms
                :is-mercado-pago="isMercadoPago"
                :mercado-pago-config="mercadoPagoConfig"
                :save-card-token="Boolean((selectedPaymentMethod?.allowTokenization || selectedPaymentMethod?.forceSaveCreditCard) && savePaymentMethod)"
                :stripe-config="stripeConfig"
                :is-stripe-method="isStripeMethod"
                :requires-card-form="requiresCardForm"
                :requires-net-pay="requiresNetPay"
                :net-pay-available="netPayAvailable"
                :card-number="cardForm.number"
                :card-expiry="cardForm.expiry"
                :card-cvv="cardForm.cvv"
                :card-holder="cardForm.holder"
                @mercadopago-success="handleMercadoPagoSuccess"
                @mercadopago-error="handleMercadoPagoError"
                @mercadopago-loading="handleMercadoPagoLoading"
                @update:cardNumber="cardForm.number = $event"
                @update:cardExpiry="cardForm.expiry = $event"
                @update:cardCvv="cardForm.cvv = $event"
                @update:cardHolder="cardForm.holder = $event"
              />
              <p v-if="infoError" class="text-[1.2rem] text-red-600">
                {{ infoError }}
              </p>
              <CheckoutPromoTotal
                :total-due="totalDue"
                :has-deposits="hasDeposits"
                :has-active-promo-codes="hasActivePromoCodes"
                :total-due-today="totalDueToday"
                :total-remaining-at-center="totalRemainingAtCenter"
                :promo-code="promoCode"
                :promo-applied="promoApplied"
                :promo-discount="promoDiscount"
                :promo-error="promoError"
                :promo-success="promoSuccess"
                :payment-loading="paymentLoading"
                :can-confirm-payment="canConfirmPayment"
                :payment-error="paymentError"
                :payment-success="paymentSuccess"
                :format-amount="formatAmount"
                @update:promoCode="promoCode = $event"
                @apply-promo="applyPromo"
                @confirm-payment="handleConfirmPayment"
              />
            </div>
          </div>
        </article>

      </div>

    </section>

    <BaseModal
      v-if="legalModal.visible"
      :title="legalModal.title"
      @close-modal="closeLegalModal"
    >
      <div v-if="legalModal.loading" class="flex items-center justify-center py-4 text-black">
        <InlineSpinner size="22" />
      </div>
      <div v-else class="space-y-4 text-[1.3rem] leading-relaxed text-black legal-modal-content" v-safe-html="legalModal.content" />
    </BaseModal>

    <section
      v-if="!hasSales"
      class="flex min-h-[60vh] xl:min-h-[70vh] items-center justify-center"
    >
      <div class="flex flex-col items-center justify-center gap-[1.6rem] rounded-[2.4rem] bg-white px-[3.2rem] py-[6.4rem] text-center shadow-sm h-full w-full max-w-[720px] xl:h-auto xl:w-auto">
        <div class="flex h-[5.6rem] w-[5.6rem] items-center justify-center rounded-full bg-blue-50 text-blue-600">
          <IconCalendar :size="32" />
        </div>
        <div class="space-y-2">
          <p class="text-[1.8rem] font-semibold text-gray-700">
            {{ $t('No pending reservations') }}
          </p>
          <p class="max-w-[42rem] text-[1.4rem] text-gray-500">
            {{ $t('We could not find a recent pre-reservation response. Start a new search to book your appointment.') }}
          </p>
        </div>
        <AppButton
          :label="$t('Search a specialist')"
          :to="{ path: '/search' }"
          variant="primary"
          class="mt-[0.8rem]"
        />
      </div>
    </section>

    <Teleport v-if="hasSales && summaryTargetExists" to="#checkout-summary-panel">
      <div class="hidden xl:block space-y-4">
        <CheckoutSummaryPanel
          :sale-cards="saleDoctorCards"
          :total-due="totalDue"
          :promo-applied="promoApplied"
          :promo-discount="promoDiscount"
          :format-amount="formatAmount"
          @remove-sale="handleRemoveSale"
        />
      </div>
    </Teleport>

    <div v-if="hasSales && !summaryTargetExists" class="space-y-4 xl:hidden">
      <CheckoutSummaryDetailed
        :sale-cards="saleDoctorCards"
        :total-due="totalDue"
        :promo-applied="promoApplied"
        :promo-discount="promoDiscount"
        :format-amount="formatAmount"
        @remove-sale="handleRemoveSale"
      />
    </div>
  </main>

</template>

<script>
import { defineComponent } from 'vue';
import CheckoutSummaryMobile from '@/components/checkout/CheckoutSummaryMobile.vue';
import CheckoutPersonalInfo from '@/components/checkout/CheckoutPersonalInfo.vue';
import CheckoutPaymentMethods from '@/components/checkout/CheckoutPaymentMethods.vue';
import CheckoutLegalOptions from '@/components/checkout/CheckoutLegalOptions.vue';
import CheckoutPaymentForms from '@/components/checkout/CheckoutPaymentForms.vue';
import CheckoutPromoTotal from '@/components/checkout/CheckoutPromoTotal.vue';
import CheckoutSummaryPanel from '@/components/checkout/CheckoutSummaryPanel.vue';
import CheckoutSummaryDetailed from '@/components/checkout/CheckoutSummaryDetailed.vue';
import AppButton from '@/components/buttons/AppButton.vue';
import BaseModal from '@/components/popup/modal/baseModal.vue';
import IconCalendar from '@/components/icons/IconCalendar.vue';
import InlineSpinner from '@/components/loader/InlineSpinner.vue';
import LoaderOverlay from '@/components/loader/loader.vue';
import { useCheckoutPrebookSuccess } from '@/composables/useCheckoutPrebookSuccess';

export default defineComponent({
  name: 'CheckoutPrebookSuccessView',
  components: {
    CheckoutSummaryMobile,
    CheckoutPersonalInfo,
    CheckoutPaymentMethods,
    CheckoutLegalOptions,
    CheckoutPaymentForms,
    CheckoutPromoTotal,
    CheckoutSummaryPanel,
    CheckoutSummaryDetailed,
    AppButton,
    BaseModal,
    IconCalendar,
    InlineSpinner,
    LoaderOverlay,
  },
  setup() {
    return useCheckoutPrebookSuccess();
  }
});
</script>


<style scoped>
.legal-modal-content :deep(p) {
  margin-bottom: 1rem;
}
</style>
